import {useNavigate} from 'react-router';
import clsx from 'clsx';
import {Autoplay} from 'swiper/modules';
import {Swiper, SwiperSlide} from 'swiper/react';
import {diApi} from '@di-shared/api/di';
import {
  AggregatedSestData,
  CreatedMaintenanceRecordRequest,
  GetMaintenanceRecordResponse,
  MaintenanceInfo,
} from '@di-shared/api/di/__generated';
import {ERROR} from '@di-shared/const';
import {emitEventLog} from '@di-shared/features/analytics';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {SestCode} from '@di-shared/features/diag/sest/const';
import useSestDetail, {ExpectedReplacementPeriodType} from '@di-shared/features/diag/sest/useSestDetail';
import {ROUTE_PATHS} from '@di-shared/routes';
import {formatNumberWithCommas} from '@di-shared/utils';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {parseDateWithFormat} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {useQueryClient, useSuspenseQuery} from '@tanstack/react-query';

interface SestDetailAirConditionerFilterProps {
  vin: string;
}

function SestDetailAirConditionerFilter({vin}: SestDetailAirConditionerFilterProps) {
  const {t} = useInferableTranslation();
  const {
    sestName,
    maintenanceType,
    replacementStatus,
    remainingDistance,
    remainingDays,
    stdDistance,
    stdDays,
    distanceReplacementStatus,
    dayReplacementStatus,
    gotoSestExchangeSelectPage,
    invalidateSestData,
    expectedReplacementPeriodType,
    replaceOdometer,
    replaceDate,
  } = useSestDetail(vin, CreatedMaintenanceRecordRequest.sestCode._03);
  const {convertDistanceSymbol, convertDistanceValue, defaultDistanceSymbol} = useUnitConverter();
  const navigate = useNavigate();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const parsedReplaceDate = parseDateWithFormat(replaceDate);
  const {data} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.maintenanceRecordController.getMostRecentMaintenanceRecord), vin, SestCode.AIR_CONDITIONER_FILTER],
    queryFn: () => diApi.maintenanceRecordController.getMostRecentMaintenanceRecord({vin, sestCode: SestCode.AIR_CONDITIONER_FILTER}),
  });
  const queryClient = useQueryClient();

  return (
    <div className="section_container">
      <div className="section_container_header">
        <h3 className="title_xxsmall">{sestName}</h3>
        {/**
         * [2026-01-06:Jeyoung Lee] FIXME: 아래 스와이퍼 div에 조금 가려짐
         * @link https://hmg.atlassian.net/browse/KCCIVOC-5363
         */}
        <button
          style={{zIndex: 2}}
          type="button"
          className="btn_setting"
          onClick={async () => {
            emitEventLog({
              event: 'consumables_exchange_update',
              data_action: 'consumables setting click',
              data_name: '에어컨필터',
            });
            const response = await ccWebviewUtils.openBottomSheetWebViewUrl<false | null | {isChange?: boolean; historyId?: string}>(
              ROUTE_PATHS.BOTTOM_SHEET_DIAG_SEST_MANAGEMENT_SETTING.getFullPath(SestCode.AIR_CONDITIONER_FILTER),
              {
                vin,
                replaceDate: parseDateWithFormat(data.data?.replaceDate),
                replaceOdometer: data.data?.replaceOdometer,
                isServiceCenterInput: data.data?.source === GetMaintenanceRecordResponse.source.SERVICE_CENTER,
                historyId: data.data?.id.toString(),
              }
            );

            if (!response) {
              return;
            }

            if (response.isChange) {
              await Promise.all([
                invalidateSestData(),
                queryClient.invalidateQueries({
                  queryKey: [
                    getQueryKey(diApi.maintenanceRecordController.getMostRecentMaintenanceRecord),
                    vin,
                    SestCode.AIR_CONDITIONER_FILTER,
                  ],
                }),
              ]);
            }
            if (response.historyId) {
              navigate(ROUTE_PATHS.DIAG_EXCHANGE_HISTORY_DETAIL_EDIT.getFullPath(vin, response.historyId));
            }
          }}
        >
          <span className="ic ic_setting_16px">
            <span className="blind">{t('T10005')}</span>
          </span>
        </button>
      </div>
      <div className="expendable_detail">
        <Swiper
          slidesPerView={'auto'}
          direction={'vertical'}
          modules={[Autoplay]}
          autoplay={{delay: 4000, disableOnInteraction: false}}
          effect={'flip'}
          observer
          observeParents
          autoHeight
          loop
          allowTouchMove={false}
        >
          <SwiperSlide>
            <p className="date_desc">
              {parsedReplaceDate && CldrDatetimeFormatter.format(parsedReplaceDate, 'yMMMd')}
              <span className="change_text">
                {replaceOdometer
                  ? formatNumberWithCommas(convertDistanceValue(replaceOdometer)) + convertDistanceSymbol(remainingDistance)
                  : ERROR.DASH + defaultDistanceSymbol}
                &nbsp;{t('T30296')}
              </span>
            </p>
          </SwiperSlide>
          {replacementStatus === MaintenanceInfo.status.REQUIRED && (
            <SwiperSlide>
              <p className="expendable_desc type_warning">
                <span className="ic ic_filled_caution_16px"></span> {t('T30025')}
              </p>
            </SwiperSlide>
          )}
          {replacementStatus === MaintenanceInfo.status.RECOMMENDED && (
            <SwiperSlide>
              <p className="expendable_desc type_warning">
                <span className="ic ic_filled_caution_16px"></span> {t('T30026')}
              </p>
            </SwiperSlide>
          )}
        </Swiper>
        <p className="expendable_desc">
          {expectedReplacementPeriodType === ExpectedReplacementPeriodType.COMBINED
            ? t('T30326')
            : expectedReplacementPeriodType === ExpectedReplacementPeriodType.DISTANCE
              ? t('T30387')
              : ''}
        </p>
        <ul className="expendable_list">
          {(maintenanceType === AggregatedSestData.maintenanceType.COMBINED ||
            maintenanceType === AggregatedSestData.maintenanceType.DISTANCE) && (
            <li
              className={clsx('expendable_item', {
                type_warning:
                  distanceReplacementStatus === MaintenanceInfo.status.REQUIRED ||
                  distanceReplacementStatus === MaintenanceInfo.status.RECOMMENDED,
              })}
            >
              <div className="expendable_icon_box">
                <span className="ic ic_air_conditioner_24px"></span>
              </div>
              <div className="expendable_info">
                <span className="expendable_info_title">
                  {t('T30028_$distance', {
                    distance: remainingDistance
                      ? formatNumberWithCommas(convertDistanceValue(remainingDistance)) + convertDistanceSymbol(remainingDistance)
                      : ERROR.DASH + defaultDistanceSymbol,
                  })}
                </span>
                <span className="expendable_info_base">
                  {t('T30029_$distance', {
                    distance: stdDistance
                      ? formatNumberWithCommas(convertDistanceValue(stdDistance)) + convertDistanceSymbol(stdDistance)
                      : ERROR.DASH + defaultDistanceSymbol,
                  })}
                </span>
              </div>
            </li>
          )}
          {(maintenanceType === AggregatedSestData.maintenanceType.COMBINED ||
            maintenanceType === AggregatedSestData.maintenanceType.DAY) && (
            <li
              className={clsx('expendable_item', {
                type_warning:
                  dayReplacementStatus === MaintenanceInfo.status.REQUIRED || dayReplacementStatus === MaintenanceInfo.status.RECOMMENDED,
              })}
            >
              <div className="expendable_icon_box">
                <span className="ic ic_history_24px"></span>
              </div>
              <div className="expendable_info">
                <span className="expendable_info_title">{t('T30030_$day', {day: formatNumberWithCommas(remainingDays)})}</span>
                <span className="expendable_info_base">{t('T30031_$day', {day: formatNumberWithCommas(stdDays)})}</span>
              </div>
            </li>
          )}
        </ul>
      </div>
      <button
        type="button"
        className="btn btn_large btn_filled btn_full"
        onClick={() => {
          emitEventLog({
            event: 'consumables_exchange_update',
            form_step: 'cta click',
            click_text: t('T30027'),
            click_location: 'body',
          });
          gotoSestExchangeSelectPage();
        }}
      >
        {t('T30027')}
      </button>
    </div>
  );
}

export default SestDetailAirConditionerFilter;
